<template>
    <div 
        v-dark
        class="concept-selector"
        :class="{
            'concept-selector--survey-answered': surveyAnswered
        }"
        v-if="conceptBYOConfig"
    >
        <ConceptBYOSurvey 
            v-if="showConceptBYOSurvey"
            @close="closeSurvey"
            @submitSurvey="submitSurvey"
            :selected-concept="clickedConcept" 
        />
        <div 
            v-dark
            class="concept-selector__concepts-head"
            :class="{
                'concept-selector__concepts-head--survey-answered': surveyAnswered,
                'concept-selector__concepts-head--search': !concepts.length
            }"
        >
            <div
                v-dark
                class="concept-selector__concepts-head-left"
                :class="{
                    'concept-selector__concepts-head-left--survey-answered': surveyAnswered
                }"
                tabindex="0"
                role="button"
                :aria-expanded="showConcepts ? 'true' : 'false'"
                @click="!surveyAnswered && toggleShowConcepts()"
                @keydown.enter="!surveyAnswered && toggleShowConcepts()"
                @mousedown.prevent
            >
                <Icon
                    v-dark
                    type="subject"
                    class="concept-selector__concepts-head-left-concept"
                />
                <label
                    id="concept-selector__concepts-head-left-concept-label"
                    class="concept-selector__concepts-head-left-concept-label"
                    :class="{
                        'concept-selector__concepts-head-left-concept-label--survey-answered': surveyAnswered
                    }"
                >Concepts</label>
                <Icon
                    v-dark
                    type="accordionArrow"
                    v-if="!surveyAnswered"
                    class="concept-selector__concepts-head-left-accordion"
                    :class="{ 'concept-selector__concepts-head-left-accordion--asc': showConcepts }"
                />
            </div>
            <div v-dark
                 class="concept-selector__concepts-head-right"
                 v-if="concepts.length && !surveyAnswered">
                <div class="concept-selector__concepts-count">
                    0 of {{ concepts.length }}
                </div>
                <Checkbox 
                    class="concept-selector__concepts-head-checkbox"
                    :is-dark-mode="isDarkMode"
                    aria-labelledby="concept-selector__concepts-head-left-concept-label"
                    @click.stop="clickConcept('all')"
                    @keydown.enter="clickConcept('all')"
                    @mousedown.prevent
                />
            </div>
        </div>
        <div v-if="surveyAnswered" class="concept-selector__concept-survey-answered">
            Thanks for your interest in building a concept quiz!
        </div>
        <div v-else-if="!concepts.length && showConcepts" class="concept-selector__concept-search">
            <Search
                v-model="conceptSearchText"
                class="concept-selector__concept-search-input"
                type="white"
                :is-dark-mode="isDarkMode"
                :placeholder="breakpoint === 'black-bear'
                    ? 'Search concepts'
                    : 'What concept would you like to focus on?'"
                @keydown.enter="submitConceptSearch"
            />
            <PocketButton
                @click="submitConceptSearch"
                :is-dark-mode="isDarkMode"
                :disabled="!conceptSearchText"
                class="concept-selector__concept-search-button"
            >
                Search
            </PocketButton>
        </div>
        <div
            v-else-if="showConcepts"
            class="concept-selector__concept-options"
            aria-live="polite"
        >
            <div 
                v-for="concept in concepts"
                :key="concept"
                v-dark
                class="concept-selector__concept-option"
                @click="clickConcept(concept)"
                @keydown.enter="clickConcept(concept)"
                @mousedown.prevent
            >
                <Checkbox 
                    class="concept-selector__concept-option-checkbox"
                    :is-dark-mode="isDarkMode"
                    @click.stop="clickConcept(concept)"
                />
                <label
                    class="concept-selector__concept-option-label"
                >
                    {{ concept }}
                </label>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'
import { Vue, Component } from 'vue-facing-decorator'
import ConceptBYOSurvey from '@/components/ConceptBYOExperiment/ConceptBYOSurvey.vue'
import { analyticsModule } from '@/store/analytics/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { screenModule } from '@/store/screen/module'

@Component({
    components: {
        Checkbox: UIKit.Checkbox,
        Icon: UIKit.Icon,
        ConceptBYOSurvey,
        PocketButton: UIKit.Button,
        PocketInput: UIKit.Input,
        Search: UIKit.Search,
    },
})
export default class ConceptSelector extends Vue {
    showConcepts = true
    showConceptBYOSurvey = false
    clickedConcept = ''
    conceptSearchText = ''
    conceptBYOConfig: {
        examMetadataId: string
        concepts?: string[]
    } | null = null
    surveyAnswered = false

    get isDarkMode () {
        return userModule.state.settings.isDarkMode
    }

    get concepts () {
        return (this.conceptBYOConfig?.concepts || [])
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    }

    get currentExamMetadata () {
        return examMetadataModule.getters.getCurrentExamMetadata()
    }

    get breakpoint () {
        return screenModule.getters.getBreakpoint()
    }

    async mounted () {
        const variant = await analyticsModule.actions.amplitudeFetchVariant('concept-byo')
        this.conceptBYOConfig = ((variant?.payload || []) as NonNullable<typeof this.conceptBYOConfig>[]).find(c =>
            c.examMetadataId === this.currentExamMetadata?.objectId
        ) || null

        // increment experiment seen count because we only want to show it to the user at most 3 times
        const seenExperimentCount = (Number(localStorage.getItem('conceptBYOExperiment_experimentSeenCount')) || 0) + 1
        localStorage.setItem('conceptBYOExperiment_experimentSeenCount', `${seenExperimentCount}`)

        analyticsModule.actions.amplitudeTrack('Concept BYO Experiment Seen', {
            conceptBYOVariant: this.concepts.length ? 'list' : 'search',
            conceptBYOVariantSeenCount: seenExperimentCount,
            examGuid: this.currentExamMetadata?.examGuid || '',
        })
    }

    toggleShowConcepts () {
        if (this.showConcepts) {
            const seenExperimentCount = Number(localStorage.getItem('conceptBYOExperiment_experimentSeenCount')) || 0
            analyticsModule.actions.amplitudeTrack('Concept BYO Experiment Hidden', {
                conceptBYOVariant: this.concepts.length ? 'list' : 'search',
                conceptBYOVariantSeenCount: seenExperimentCount,
                examGuid: this.currentExamMetadata?.examGuid || '',
            })
        }
        this.showConcepts = !this.showConcepts
    }

    submitConceptSearch () {
        this.clickedConcept = this.conceptSearchText
        this.showConceptBYOSurvey = true
    }

    clickConcept (concept: string) {
        this.showConceptBYOSurvey = true
        this.clickedConcept = concept
    }

    closeSurvey () {
        this.showConceptBYOSurvey = false
    }
    
    submitSurvey () {
        this.showConceptBYOSurvey = false
        this.surveyAnswered = true
    }
}
</script>
<style lang="scss" scoped>
.concept-selector {
    border: 1px solid $gray-divider;
    background: $white;
    border-radius: 6px;

    &--dark {
        background: $brand-black;
        color: $white;
        border: 0;
    }

    &--survey-answered {
        background: $gray-background;

        &--dark {
            background: $charcoal;
            border: 1px solid $ash;
        }
    }

    &__concept-options {
        max-height: 210px;
        overflow: auto;
    }

    &__concept-option {
        color: $brand-black;
        padding: 14px 50px 13px 15px;
        border-bottom: 1px solid $gray-divider;
        cursor: pointer;
        user-select: none;
        position: relative;

        &--dark {
            color: $pewter;
            border-bottom-color: $ash;
            padding: 14px 51px 13px 16px;
        }

        &--selected {
            color: $brand-black;

            &--dark {
                color: $white;
            }
        }

        &:last-child {
            border-bottom: 0;
        }

        svg {
            color: $brand-blue;
            position: absolute;
            width: 16px;
            height: 13px;
            top: 19px;
            right: 20px;
        }
    }

    &__concept-option-checkbox {
        position: absolute;
        top: 50%;
        margin-top: -10.5px;
        right: 12px;
    }

    &__concept-option-label {
        cursor: pointer;
    }

    &__concepts-head {
        display: flex;
        justify-content: space-between;
        padding: 16px 12px 14px 15px;
        align-items: center;
        border-bottom: 1px solid $gray-divider;

        &--search,
        &--survey-answered {
            border-bottom: 0px solid transparent;
        }

        &--dark {
            padding: 16px 13px 14px 16px;
            border-bottom-color: $ash;
        }
    }

    &__concepts-head-left {
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        flex: 1;
        outline: none;
        position: relative;

        span {
            display: block;
            font-size: 15px;
            line-height: 20px;
        }

        &:focus::before {
            content: '';
            left: -4px;
            top: -4px;
            width: 100%;
            height: calc(100% + 8px);
            border: 1px solid $brand-blue;
            position: absolute;
            border-radius: 5px;
        }

        &--dark:focus::before {
            border-color: $banana-bread;
        }

        &--survey-answered {
            cursor: default;
        }
    }

    &__concepts-head-left-concept {
        margin-right: 8px;
        height: 21px;

        &--dark {
            color: $pewter;
        }
    }

    &__concepts-head-left-concept-label {
        cursor: pointer;

        &--survey-answered {
            cursor: default;
        }
    }

    &__concepts-head-left-accordion {
        color: $brand-blue;
        margin-left: 6px;

        &--asc {
            transform: rotate(180deg);
        }

        &--dark {
            color: $white;
        }
    }

    &__concepts-head-right {
        display: flex;
        align-items: center;
        color: $slate-01;
        font-size: 14px;
        line-height: 17px;

        &--dark {
            color: $fog;
        }
    }

    &__concepts-count {
        margin-right: 8px;
    }

    &__concept-search {
        display: flex;
        width: 100%;
        justify-content: space-between;
        padding: 0 18px 19px;
        box-sizing: border-box;
    }

    &__concept-search-input {
        width: 100%;
    }

    &__concept-search-button {
        height: 36px;
        margin-left: 12px;
        padding: 8px 10px;
    }

    &__concept-survey-answered {
        padding: 0 18px 17px;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;
    }
}
</style>